// src/components/SyncIndicator.jsx
//
// Shows outbox sync status in the nav bar.
// - Pending ops: small pill with count
// - Stuck ops: expandable banner with dismiss option
// - Synced: hidden (no visual noise when everything's clean)
//
// Position: rightmost element in nav, before sign-out button.
// Works across all zones via inline styles (no zone dependency).

import { useState, useEffect } from 'react'
import outbox from '../lib/outbox'
import { CloudOff, Check, AlertTriangle, X, RefreshCw } from 'lucide-react'
import useOnlineStatus from '../lib/useOnlineStatus'

export default function SyncIndicator() {
  const isOnline = useOnlineStatus()
  const [pending, setPending] = useState(0)
  const [stuck, setStuck] = useState([])
  const [showStuckBanner, setShowStuckBanner] = useState(false)
  const [justSynced, setJustSynced] = useState(false)

  // Subscribe to outbox count changes
  useEffect(() => {
    // Get initial counts
    outbox.pendingCount().then(setPending)
    outbox.getStuck().then(setStuck)

    const unsub = outbox.subscribe((count) => {
      setPending(count)
      // Also refresh stuck list on any change
      outbox.getStuck().then(setStuck)

      // Flash "synced" indicator when count drops to 0
      if (count === 0) {
        setJustSynced(true)
        setTimeout(() => setJustSynced(false), 2000)
      }
    })

    // Poll stuck ops every 30s (they don't trigger subscribe)
    const stuckInterval = setInterval(() => {
      outbox.getStuck().then(setStuck)
    }, 30000)

    return () => { unsub(); clearInterval(stuckInterval) }
  }, [])

  const handleDismissStuck = async (id) => {
    await outbox.dismissStuck(id)
    setStuck(prev => prev.filter(s => s.id !== id))
  }

  const handleRetryAll = () => {
    outbox.drain()
  }

  // Nothing to show — clean state
  if (pending === 0 && stuck.length === 0 && !justSynced && isOnline) {
    return null
  }

  return (
    <>
      {/* ── NAV PILL ── */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '4px' }}>

        {/* Just synced flash */}
        {justSynced && pending === 0 && stuck.length === 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '3px 8px', fontSize: '10px',
            background: 'rgba(0, 200, 100, .15)',
            color: '#4dd6c2',
            border: '1px solid rgba(0, 200, 100, .3)',
            fontFamily: 'inherit',
            letterSpacing: '0.05em',
            animation: 'sync-fade 2s ease forwards',
          }}>
            <Check size={10} /> SYNCED
          </div>
        )}

        {/* Offline indicator */}
        {!isOnline && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '3px 8px', fontSize: '10px',
            background: 'rgba(255, 106, 61, .12)',
            color: '#ff6a3d',
            border: '1px solid rgba(255, 106, 61, .25)',
            fontFamily: 'inherit',
            letterSpacing: '0.05em',
          }}>
            <CloudOff size={10} /> OFFLINE
          </div>
        )}

        {/* Pending count pill */}
        {pending > 0 && (
          <button onClick={handleRetryAll} style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '3px 8px', fontSize: '10px',
            background: 'rgba(255, 204, 51, .12)',
            color: '#ffcc33',
            border: '1px solid rgba(255, 204, 51, .25)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.05em',
          }} title="Pending sync — click to retry">
            <RefreshCw size={10} /> {pending}
          </button>
        )}

        {/* Stuck ops warning */}
        {stuck.length > 0 && (
          <button onClick={() => setShowStuckBanner(!showStuckBanner)} style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '3px 8px', fontSize: '10px',
            background: 'rgba(255, 42, 42, .12)',
            color: '#ff4136',
            border: '1px solid rgba(255, 42, 42, .25)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            letterSpacing: '0.05em',
          }} title="Failed operations — click to view">
            <AlertTriangle size={10} /> {stuck.length} STUCK
          </button>
        )}
      </div>

      {/* ── STUCK OPS BANNER ── */}
      {showStuckBanner && stuck.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          zIndex: 200,
          background: '#1a0808',
          borderBottom: '2px solid rgba(255, 42, 42, .4)',
          padding: '12px 16px',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '12px',
          color: '#ff6a6a',
        }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} />
              <span style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {stuck.length} FAILED OPERATION{stuck.length !== 1 ? 'S' : ''}
              </span>
            </div>
            <button onClick={() => setShowStuckBanner(false)} style={{ background: 'none', border: 'none', color: '#ff6a6a', cursor: 'pointer' }}>
              <X size={16} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {stuck.map(op => (
              <div key={op.id} className="flex items-center justify-between" style={{
                background: 'rgba(255, 42, 42, .06)',
                border: '1px solid rgba(255, 42, 42, .15)',
                padding: '8px 12px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ color: '#ff9a9a', fontSize: '11px' }}>{op.op}</span>
                  {op.last_error && (
                    <p style={{ color: '#994444', fontSize: '10px', marginTop: '2px' }}>{op.last_error}</p>
                  )}
                  <p style={{ color: '#663333', fontSize: '9px', marginTop: '2px' }}>
                    {new Date(op.created_at).toLocaleTimeString()} · {op.attempts} attempt{op.attempts !== 1 ? 's' : ''}
                  </p>
                </div>
                <button onClick={() => handleDismissStuck(op.id)} style={{
                  background: 'rgba(255, 42, 42, .1)',
                  border: '1px solid rgba(255, 42, 42, .2)',
                  color: '#ff6a6a',
                  padding: '4px 10px',
                  fontSize: '10px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  letterSpacing: '0.05em',
                }}>
                  DISMISS
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fade animation for synced flash */}
      <style>{`@keyframes sync-fade { 0% { opacity: 1; } 70% { opacity: 1; } 100% { opacity: 0; } }`}</style>
    </>
  )
}
